import { useState, useEffect } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { fornecedorAdapter } from "@/data";
import type { Fornecedor } from "@/types/fornecedor";

interface FornecedorComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
}

export function FornecedorCombobox({ value, onValueChange }: FornecedorComboboxProps) {
  const [open, setOpen] = useState(false);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadFornecedores();
  }, [search]);

  const loadFornecedores = async () => {
    try {
      const response = await fornecedorAdapter.list({ search, filters: { ativo: true } });
      setFornecedores(response.items);
    } catch (error) {
      console.error("Erro ao carregar fornecedores:", error);
    }
  };

  const selectedFornecedor = fornecedores.find((f) => f.id_fornecedor === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between">
          {selectedFornecedor ? selectedFornecedor.nome : "Selecione um fornecedor..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar fornecedor..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>Nenhum fornecedor encontrado.</CommandEmpty>
            <CommandGroup>
              {fornecedores.map((fornecedor) => (
                <CommandItem
                  key={fornecedor.id_fornecedor}
                  value={fornecedor.nome}
                  onSelect={() => {
                    onValueChange(fornecedor.id_fornecedor);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === fornecedor.id_fornecedor ? "opacity-100" : "opacity-0")} />
                  {fornecedor.nome}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
